<template>
  <div class="share-page">
    <AppHeader v-model="keyword" placeholder="搜索资源标题" :search-href="pageHref(1, selectedDrive.attrType, keyword)" />
    <AppNavTabs :active-id="activeTabId" />

    <AppDriveFilter :driver-map="driverMap" :selected-attr-type="selectedDrive.attrType" :keyword="keyword"
      :page-href="pageHref" />

    <section class="share-list">
      <article v-for="(item, idx) in displayedShares" :key="item.id" class="share-card"
        :style="{ animationDelay: `${idx * 0.08}s` }">
        <div class="card-top">
          <h2 class="share-title">{{ item.title }}</h2>
        </div>
        <div class="card-mid">
          <div class="drive-type">
            <span class="label">网盘类型</span>
            <span class="value">{{ item.driveType?.driverName || "未知网盘" }}</span>
          </div>
          <a class="drive-btn" :href="`/source/${item.id}`">
            查看地址
          </a>
        </div>
        <div class="card-footer">
          <div class="user">
            <img class="avatar" :src="item.avatar" :alt="item.user" />
            <div class="user-meta">
              <div class="name">{{ item.user }}</div>
              <div class="time">{{ item.time }}</div>
            </div>
          </div>
          <div class="stats">
            <div class="stat">
              <span class="stat-value">{{ item.likeCount }}</span>
              <span class="stat-label">点赞</span>
            </div>
            <div class="stat">
              <span class="stat-value">{{ item.collectCount }}</span>
              <span class="stat-label">兑换</span>
            </div>
            <div class="stat">
              <span class="stat-value">{{ item.subcribeCount }}</span>
              <span class="stat-label">关注</span>
            </div>
          </div>
        </div>
      </article>
      <div v-if="displayedShares.length === 0" class="empty">
        暂无该网盘资源
      </div>
    </section>

    <nav class="pagination" aria-label="分页">
      <a v-if="page > 1" class="page-btn" :href="pageHref(1)">
        首页
      </a>
      <span v-else class="page-btn disabled">首页</span>

      <a v-if="page > 1" class="page-btn" :href="pageHref(page - 1)">
        上一页
      </a>
      <span v-else class="page-btn disabled">上一页</span>

      <a v-if="page < totalPages" class="page-btn" :href="pageHref(page + 1)">
        下一页
      </a>
      <span v-else class="page-btn disabled">下一页</span>

      <a v-if="page < totalPages" class="page-btn" :href="pageHref(totalPages)">
        尾页
      </a>
      <span v-else class="page-btn disabled">尾页</span>
    </nav>

    <AppFooter active="home" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { normalizeAvatarUrl } from "~/composables/user";
import { driverMap, type DriverInfo } from "~/composables/source";

const categoryNameMap: Record<string, string> = {
  "2": "影视",
  "7": "动漫",
  "8": "书籍",
  "9": "软件",
  "10": "教程",
  "11": "游戏",
  "12": "系统",
  "13": "图片",
  "14": "音乐",
  "15": "素材",
  "16": "其他",
};

const seoDescription =
  "我为人人，人人为我,用户共建，资源共享,分享一次，受益无数,人人贡献，资源无穷,因分享而强大";

type ShareItem = {
  id: string;
  title: string;
  driveType?: DriverInfo;
  driveUrl: string;
  avatar: string;
  user: string;
  time: string;
  likeCount: number;
  collectCount: number;
  subcribeCount: number;
};


const selectedDrive = ref(driverMap[0]);
const pageSize = 10;
const route = useRoute();
const config = useRuntimeConfig();

type SourceAttr = {
  attrType?: number;
  attrData?: string;
};

type SourceItem = {
  sourceId?: string;
  title?: string;
  attrs?: SourceAttr[];
  driverTypes?: number[];
  createTime?: string;
  userHeadUrl?: string;
  nickName?: string;
  likeCount?: number;
  collectCount?: number;
  curUserSubcribe?: boolean;
};

type SourcePage = {
  total?: number;
  currentNumber?: number;
  pageSize?: number;
  list?: SourceItem[];
};

type SourceResponse = {
  code: number;
  data?: SourcePage;
};

const requestedPage = computed(() => {
  const raw = Number(route.query.PageNum ?? 1);
  return Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 1;
});

const selectedAttrType = computed(() => {
  const raw = route.query.attrType;
  const value = Array.isArray(raw) ? raw[0] : raw;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 2000;
  return parsed;
});

const activeTabId = computed(() => {
  const raw = route.params.id;
  const value = Array.isArray(raw) ? raw[0] : raw;
  return typeof value === "string" ? value : "0";
});

const categoryName = computed(() => {
  return categoryNameMap[activeTabId.value] || "资源";
});

const seoTitle = computed(() => `${categoryName.value}类别`);
const seoKeywords = computed(
  () => `${categoryName.value}类别,资源,网盘，共建，分享`
);

useSeoMeta({
  title: seoTitle,
  ogTitle: seoTitle,
  description: seoDescription,
  ogDescription: seoDescription,
});

useHead({
  meta: [{ name: "keywords", content: seoKeywords }],
});

const selectedCategoryId = computed(() => {
  const raw = route.params.id;
  const value = Array.isArray(raw) ? raw[0] : raw;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed === 0) return undefined;
  return parsed;
});

const keyword = ref("");
const selectedLikeTitle = computed(() => {
  const raw = route.query.likeTitle;
  const value = Array.isArray(raw) ? raw[0] : raw;
  return typeof value === "string" ? value : "";
});

watch(
  selectedLikeTitle,
  (value) => {
    keyword.value = value;
  },
  { immediate: true }
);

const requestBody = computed(() => {
  const body: Record<string, any> = {
    pageNum: requestedPage.value,
    pageSize,
    likeTitle: selectedLikeTitle.value || "",
  };
  if (selectedAttrType.value !== 2000) {
    body.attrTypes = [selectedAttrType.value];
  }
  if (selectedCategoryId.value !== undefined) {
    body.categoryIds = [selectedCategoryId.value];
  }
  return body;
});

const { data } = await useAsyncData(
  `source-page-${requestedPage.value}-${selectedAttrType.value}-${pageSize}`,
  () =>
    $api_Source_GetSourceByPage({
      method: "POST",
      body: requestBody.value,
    }) as Promise<SourceResponse>,
  { server: true }
);
const response = data.value;

const totalPages = computed(() => {
  const total = response.data?.total ?? 0;
  const size = response.data?.pageSize ?? pageSize;
  return Math.max(1, Math.ceil(total / size));
});

const page = computed(() => {
  return Math.min(requestedPage.value, totalPages.value);
});

const getDrive = (attrs: SourceAttr[] | undefined, driverTypes: number[] | undefined) => {
  let driveType: DriverInfo | undefined;
  if (Array.isArray(driverTypes) && driverTypes.length > 0) {
    driveType = driverMap.find((item) => item.attrType === driverTypes[0]);
  }
  if (!attrs?.length) return { driveUrl: "", driveType };
  const match = attrs.find(
    (item) =>
      typeof item.attrType === "number" &&
      item.attrType > 2000 &&
      item.attrType < 3000 &&
      item.attrData
  );
  if (match?.attrData) {
    if (!driveType) {
      driveType = driverMap.find((item) => item.attrType === match.attrType);
    }
    return { driveUrl: match.attrData, driveType };
  }
  return { driveUrl: "", driveType };
}



const mapSourceItem = (item: SourceItem): ShareItem => {
  const driverInfo = getDrive(item.attrs, item.driverTypes);
  return {
    id: String(item.sourceId ?? ""),
    title: item.title ?? "",
    driveType: driverInfo.driveType,
    driveUrl: driverInfo.driveUrl,
    avatar: normalizeAvatarUrl(item.userHeadUrl, config) || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=120&q=80",
    user: item.nickName || "匿名分享",
    time: (item.createTime || "").replace("T", " ").slice(0, 16),
    likeCount: item.likeCount ?? 0,
    collectCount: item.collectCount ?? 0,
    subcribeCount: item.curUserSubcribe ? 1 : 0,
  };
};

const sourceList = computed(() => {
  const response = data.value;
  if (!response || (response.code !== 0 && response.code !== 1)) return [];
  return response.data?.list ?? [];
});

const mappedShares = computed(() => {
  return sourceList.value.map(mapSourceItem);
});

const displayedShares = computed(() => {
  if (selectedDrive.value.attrType === 2000) {
    return mappedShares.value;
  }
  return mappedShares.value.filter(
    (item) => item.driveType?.attrType === selectedDrive.value.attrType
  );
});

const pageHref = (
  targetPage: number,
  attrType = selectedDrive.value.attrType,
  likeTitle = selectedLikeTitle.value
) => {
  const query: Record<string, any> = {
    ...route.query,
    PageNum: String(targetPage),
  };
  if (attrType !== 2000) {
    query.attrType = String(attrType);
  } else {
    delete query.attrType;
  }
  if (likeTitle) {
    query.likeTitle = String(likeTitle);
  } else {
    delete query.likeTitle;
  }
  const search = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      value.forEach((entry) => search.append(key, String(entry)));
    } else {
      search.set(key, String(value));
    }
  });
  const qs = search.toString();
  return qs ? `${route.path}?${qs}` : route.path;
};

watch(
  selectedAttrType,
  (value) => {
    const match = driverMap.find((item) => item.attrType === value);
    selectedDrive.value = match ?? driverMap[0];
  },
  { immediate: true }
);



</script>

<style scoped>
.share-page {
  --ink: #1f1b20;
  --muted: #6a5f63;
  --accent: #f2694b;
  --card: #ffffff;
  --shadow: rgba(242, 105, 75, 0.18);
  /* min-height: 100vh; */
  padding: calc(var(--app-header-height, 50px) + var(--app-navtabs-height, 40px)) 5px 100px;
  background:
    radial-gradient(circle at top left, rgba(255, 215, 196, 0.9), transparent 55%),
    radial-gradient(circle at 80% 0%, rgba(255, 232, 210, 0.8), transparent 48%),
    linear-gradient(180deg, #f9f3ec 0%, #fff 70%);
  color: var(--ink);
}

.share-list {
  display: grid;
  gap: 14px;
}

.share-card {
  background: var(--card);
  border-radius: 18px;
  padding: 16px;
  box-shadow: 0 16px 32px var(--shadow);
  display: grid;
  gap: 12px;
  opacity: 0;
  transform: translateY(14px);
  animation: rise 0.6s ease forwards;
}

.empty {
  text-align: center;
  font-size: 12px;
  color: var(--muted);
  padding: 12px 0 6px;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px 0 8px;
}

.page-btn {
  border: 1px solid #f3c7ba;
  background: #fff;
  color: #7a5d57;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  text-decoration: none;
}

.page-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  padding: 4px 2px;
  scrollbar-width: none;
}

.page-numbers::-webkit-scrollbar {
  display: none;
}

.page-number {
  min-width: 28px;
  text-align: center;
  padding: 6px 8px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #7a5d57;
  text-decoration: none;
  border: 1px solid transparent;
}

.page-number.active {
  background: #fff0ea;
  color: #c74d33;
  border-color: #f3c7ba;
}

.card-top {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.share-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.4;
}

.card-mid {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #fff6f1;
  padding: 10px 12px;
  border-radius: 14px;
}

.drive-type {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.label {
  font-size: 11px;
  color: var(--muted);
}

.value {
  font-size: 14px;
  font-weight: 700;
}

.drive-btn {
  border: none;
  background: var(--accent);
  color: #fff;
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 10px 20px rgba(242, 105, 75, 0.25);
}

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.user {
  display: flex;
  align-items: center;
  gap: 10px;
}

.avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #ffe1d4;
}

.user-meta {
  display: grid;
  gap: 2px;
}

.stats {
  display: flex;
  gap: 12px;
  align-items: center;
}

.stat {
  display: grid;
  gap: 2px;
  text-align: center;
}

.stat-value {
  font-size: 12px;
  font-weight: 700;
  color: #2b2529;
}

.stat-label {
  font-size: 11px;
  color: var(--muted);
}

.name {
  font-size: 13px;
  font-weight: 700;
}

.time {
  font-size: 11px;
  color: var(--muted);
}

:global(.share-drive-modal .ant-modal-confirm) {
  max-width: 320px;
}

:global(.share-drive-modal .ant-modal-confirm-body) {
  padding-bottom: 6px;
}

:global(.share-drive-modal .ant-modal-confirm-btns) {
  display: flex;
  justify-content: center;
  gap: 12px;
}

:global(.share-drive-modal .ant-btn-primary) {
  background: var(--accent);
  border-color: var(--accent);
}

:global(.share-drive-modal .ant-btn-primary:hover) {
  background: #f27f66;
  border-color: #f27f66;
}

:global(.drive-modal-body) {
  display: grid;
  gap: 12px;
}

:global(.drive-modal-row) {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #2b2529;
}

:global(.drive-modal-label) {
  color: #7a6d6f;
}

:global(.drive-modal-url) {
  font-size: 12px;
  color: #f2694b;
  word-break: break-all;
}

@keyframes rise {
  from {
    opacity: 0;
    transform: translateY(16px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (min-width: 768px) {
  .share-page {
    padding: 28px 22% 120px;
  }

  .page-title {
    font-size: 28px;
  }
}
</style>
